import { useEffect } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { useSession } from '@/contexts/SessionContext';
import { useActiveMatches } from './useActiveMatches';
import type { AwardedPrize } from './lobby.types';

export type LeaderboardEntry = {
  rank: number | null;
  userId: string;
  username: string | null;
  goalCount: number;
  shotCount: number;
  tackleCount: number;
  interceptionCount: number;
  score: number;
  prize: AwardedPrize | null;
};

export type LeaderboardResponse = {
  entries: LeaderboardEntry[];
  me: LeaderboardEntry | null;
  page: number;
  limit: number;
  total: number;
};

export type LeaderboardPeriod = 'week' | 'month';

export const leaderboardQueryKey = (period: LeaderboardPeriod, id: string | null) =>
  ['squads', 'leaderboard', period, id] as const;

/**
 * Leaderboard for a round or a month. Both endpoints return the same shape, so
 * one hook and one renderer serve both. Polls faster while a match is live so
 * ranks move with the scores; otherwise refreshes on the lobby cadence.
 */
const fetchLeaderboard = async (period: LeaderboardPeriod, id: string, page: number) =>
  (await apiClient.get(
    period === 'week'
      ? `/api/squads/rounds/${id}/leaderboard`
      : `/api/squads/months/${id}/leaderboard`,
    { params: { page } }
  )) as unknown as LeaderboardResponse;

/** Pages kept in cache; older ones drop off as the user scrolls on, so a huge board never refetches in full. */
const MAX_PAGES = 10;
const nextPage = (last: LeaderboardResponse) =>
  last.page * last.limit < last.total ? last.page + 1 : undefined;
const prevPage = (first: LeaderboardResponse) => (first.page > 1 ? first.page - 1 : undefined);

export function useLeaderboard(period: LeaderboardPeriod, id: string | null) {
  const session = useSession();
  const { data: matchesData } = useActiveMatches();

  const hasLiveMatch =
    matchesData?.matches.some(m => m.status === 'live' || m.status === 'half_time') ?? false;

  return useInfiniteQuery({
    queryKey: leaderboardQueryKey(period, id),
    queryFn: ({ pageParam }) => fetchLeaderboard(period, id as string, pageParam),
    initialPageParam: 1,
    getNextPageParam: nextPage,
    getPreviousPageParam: prevPage,
    maxPages: MAX_PAGES,
    enabled: session.status === 'ready' && id !== null,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    refetchInterval: hasLiveMatch ? 30_000 : 60_000,
  });
}

/**
 * Warm the boards a swipe or a toggle can reach: the rounds either side of the
 * one on screen, and the month the current round counts towards.
 *
 * Swiping to a week never opened before is otherwise a cold fetch with the
 * sheet sitting empty. The month is warmed for the same reason — the period
 * toggle changes the query key, so without this the monthly board was only
 * ever cached by opening it once.
 */
export function usePrefetchLeaderboards(
  prevRoundId: string | null,
  nextRoundId: string | null,
  monthId: string | null
) {
  const queryClient = useQueryClient();
  const session = useSession();

  useEffect(() => {
    if (session.status !== 'ready') return;
    const warm = (period: LeaderboardPeriod, id: string) =>
      queryClient.prefetchInfiniteQuery({
        queryKey: leaderboardQueryKey(period, id),
        queryFn: ({ pageParam }) => fetchLeaderboard(period, id, pageParam),
        initialPageParam: 1,
        staleTime: 30_000,
      });

    for (const id of [prevRoundId, nextRoundId]) {
      if (id) warm('week', id);
    }
    if (monthId) warm('month', monthId);
  }, [queryClient, session.status, prevRoundId, nextRoundId, monthId]);
}
